import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api';
import { getPhases, getSeasonsByTeam, assignPhase, getStandings, createStanding, updateStanding, deleteStanding } from '../api/seasons';
import { formatDate } from '../utils/dateUtils';
import { useCurrentTeam } from '../hooks/useCurrentTeam';
import { useCurrentPlayer } from '../hooks/useCurrentPlayer';
import './MatchHistory.css';

const PHASE_ORDER = ['MASTER_FINAL', 'SEGUNDA_FASE_A', 'SEGUNDA_FASE_B', 'PRIMERA_FASE'];

const PHASE_STYLE = {
    PRIMERA_FASE:   { color: '#0F172A', bg: '#eff6ff' },
    SEGUNDA_FASE_A: { color: '#8b5cf6', bg: '#f5f3ff' },
    SEGUNDA_FASE_B: { color: '#d97706', bg: '#fffbeb' },
    MASTER_FINAL:   { color: '#D2F800', bg: '#f0fdf4' },
    SIN_FASE:       { color: '#cbd5e1', bg: '#f8fafc' },
};

const OUR_TEAM_NAME = 'Panteres Grogues';


const getTopPhase = (pastMatches) => {
    const grouped = pastMatches.reduce((acc, m) => {
        const key = m.phase || 'SIN_FASE';
        if (!acc[key]) acc[key] = [];
        acc[key].push(m);
        return acc;
    }, {});
    for (const phase of PHASE_ORDER) {
        if (grouped[phase]) return phase;
    }
    if (grouped['SIN_FASE']) return 'SIN_FASE';
    return null;
};

export default function MatchHistory({
    prefetchedMatches,
    prefetchedPhases,
    prefetchedSeasonName,
    prefetchedSeasonId,
    parentLoading = false,
}) {
    const [matches, setMatches]                 = useState([]);
    const [allMatches, setAllMatches]           = useState([]);
    const [phases, setPhases]                   = useState([]);
    const [seasonName, setSeasonName]           = useState('');
    const [currentSeasonId, setCurrentSeasonId] = useState(null);
    const [loading, setLoading]                 = useState(true);
    const [openPhases, setOpenPhases]           = useState({});
    const [editingId, setEditingId]             = useState(null);
    const [editPhase, setEditPhase]             = useState('');

    const [standings, setStandings]             = useState({});
    const [editingStandingId, setEditingStandingId] = useState(null);
    const [editingPts, setEditingPts]           = useState('');

    const navigate    = useNavigate();
    const { team }    = useCurrentTeam();
    const { player }  = useCurrentPlayer();
    const isCaptain   = ['ROLE_CAPTAIN', 'ROLE_COORDINATOR'].includes(player?.role);

    useEffect(() => {
        if (!parentLoading && prefetchedMatches && prefetchedPhases) {
            const today = new Date();
            const past  = prefetchedMatches
                .filter(m => {
                    if (!m.date || !m.time) return false;
                    try {
                        const dt = new Date(`${m.date}T${m.time}`);
                        return new Date() >= new Date(dt.getTime() + 90 * 60000);
                    } catch (e) {
                        const d = new Date(m.date);
                        d.setHours(0, 0, 0, 0);
                        return d < today;
                    }
                })
                .sort((a, b) => new Date(b.date) - new Date(a.date));

            setMatches(past);
            setAllMatches(prefetchedMatches);
            setPhases(prefetchedPhases);
            if (prefetchedSeasonName) setSeasonName(prefetchedSeasonName);
            if (prefetchedSeasonId)   setCurrentSeasonId(prefetchedSeasonId);
            const top = getTopPhase(past);
            setOpenPhases(top ? { [top]: true } : {});
            setLoading(false);
            return;
        }

        const load = async () => {
            try {
                const [matchData, phaseData, seasonsData] = await Promise.all([
                    apiClient.get(`/teams/${team?.id}/matchdays`).then(r => r.data),
                    getPhases(),
                    team?.id ? getSeasonsByTeam(team.id) : Promise.resolve([]),
                ]);

                const today = new Date();
                const past  = matchData
                    .filter(m => {
                        if (!m.date || !m.time) return false;
                        try {
                            const dt = new Date(`${m.date}T${m.time}`);
                            return new Date() >= new Date(dt.getTime() + 90 * 60000);
                        } catch (e) {
                            const d = new Date(m.date);
                            d.setHours(0, 0, 0, 0);
                            return d < today;
                        }
                    })
                    .sort((a, b) => new Date(b.date) - new Date(a.date));

                setMatches(past);
                setAllMatches(matchData);
                setPhases(phaseData);
                if (seasonsData?.length > 0) {
                    setSeasonName(seasonsData[0].name);
                    setCurrentSeasonId(seasonsData[0].id);
                }

                const top = getTopPhase(past);
                setOpenPhases(top ? { [top]: true } : {});
            } catch (err) {
                console.error('Error cargando historial', err);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [team?.id, parentLoading, prefetchedMatches, prefetchedPhases, prefetchedSeasonName, prefetchedSeasonId]);

    const syncStandings = useCallback(async (currentMatches) => {
        if (!team?.id || currentMatches.length === 0) return;

        try {
            const allStandings = await getStandings(team.id);
            const byPhase = allStandings.reduce((acc, s) => {
                if (!acc[s.phase]) acc[s.phase] = [];
                acc[s.phase].push(s);
                return acc;
            }, {});

            const updated = { ...byPhase };

            for (const phaseKey of PHASE_ORDER) {
                const phaseMatches = currentMatches.filter(m => m.phase === phaseKey);
                if (phaseMatches.length === 0) continue;

                const existing = new Set(
                    (byPhase[phaseKey] || []).map(s => s.rivalName.toLowerCase().trim())
                );

                const candidates = [
                    OUR_TEAM_NAME,
                    ...new Set(phaseMatches.map(m => m.opponentName).filter(Boolean)),
                ];

                const toCreate = candidates.filter(name => !existing.has(name.toLowerCase().trim()));

                if (toCreate.length > 0) {
                    const created = await Promise.all(
                        toCreate.map(name =>
                            createStanding(team.id, { phase: phaseKey, rivalName: name, points: 0 })
                        )
                    );
                    updated[phaseKey] = [...(byPhase[phaseKey] || []), ...created]
                        .sort((a, b) => b.points - a.points);
                }
            }

            setStandings(updated);
        } catch (err) {
            console.error('Error sincronizando clasificaciones', err);
        }
    }, [team?.id]);

    useEffect(() => {
        if (!loading && allMatches.length > 0 && team?.id) {
            syncStandings(allMatches);
        }
    }, [loading, allMatches, team?.id, syncStandings]);

    const grouped = matches.reduce((acc, m) => {
        const key = m.phase || 'SIN_FASE';
        if (!acc[key]) acc[key] = [];
        acc[key].push(m);
        return acc;
    }, {});

    const sortedKeys = [
        ...PHASE_ORDER.filter(p => grouped[p]),
        ...(grouped['SIN_FASE'] ? ['SIN_FASE'] : []),
    ];

    const togglePhase = (key) => {
        setOpenPhases(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const phasePoints = (key) => {
        const ms = grouped[key] || [];
        return ms.reduce((sum, m) => sum + (m.pointsTeam ?? 0), 0);
    };

    const getResultBadge = (teamPoints, opponentPoints) => {
        if (!teamPoints && !opponentPoints)
            return <span className="badge pending">Pendiente</span>;
        if (teamPoints > opponentPoints) return <span className="badge win">Victoria</span>;
        if (teamPoints < opponentPoints) return <span className="badge loss">Derrota</span>;
        return <span className="badge draw">Empate</span>;
    };

    const phaseLabel = (key) => {
        const found = phases.find(p => p.value === key);
        return found ? found.label : 'Sin fase asignada';
    };

    const startEdit = (e, match) => {
        e.stopPropagation();
        setEditingId(match.id);
        setEditPhase(match.phase || '');
    };

    const cancelEdit = (e) => {
        e.stopPropagation();
        setEditingId(null);
    };

    const saveEdit = async (e, matchId) => {
        e.stopPropagation();
        try {
            const updated = await assignPhase(matchId, currentSeasonId || null, editPhase || null);
            setMatches(prev => prev.map(m => m.id === matchId ? { ...m, ...updated } : m));
        } catch (err) {
            console.error('Error guardando fase', err);
        } finally {
            setEditingId(null);
        }
    };

    const startEditStanding = (e, entry) => {
        e.stopPropagation();
        setEditingStandingId(entry.id);
        setEditingPts(String(entry.points));
    };

    const cancelEditStanding = () => {
        setEditingStandingId(null);
        setEditingPts('');
    };

    const saveStandingPts = async (phaseKey, id) => {
        if (!team?.id) return;
        try {
            const saved = await updateStanding(team.id, id, { points: parseInt(editingPts, 10) || 0 });
            setStandings(prev => ({
                ...prev,
                [phaseKey]: (prev[phaseKey] || [])
                    .map(s => s.id === id ? saved : s)
                    .sort((a, b) => b.points - a.points),
            }));
        } catch (err) {
            console.error('Error actualizando puntos', err);
        } finally {
            cancelEditStanding();
        }
    };

    const handleDeleteStanding = async (e, phaseKey, id) => {
        e.stopPropagation();
        if (!team?.id) return;
        try {
            await deleteStanding(team.id, id);
            setStandings(prev => ({
                ...prev,
                [phaseKey]: (prev[phaseKey] || []).filter(s => s.id !== id),
            }));
        } catch (err) {
            console.error('Error eliminando clasificación', err);
        }
    };

    if (loading) return <div className="history-loader">Cargando historial...</div>;

    if (matches.length === 0) return (
        <div className="empty-history">
            <i className="fa fa-history"></i>
            <p>Aún no se han jugado partidos.</p>
        </div>
    );

    return (
        <div className="match-history-container">
            <div className="history-header">
                <h3>
                    <i className="fa fa-trophy"></i> Historial
                    {seasonName && <span className="season-tag">{seasonName}</span>}
                </h3>
            </div>

            {sortedKeys.length === 0 && (
                <p className="no-phase-matches">No hay partidos en el historial aún.</p>
            )}

            {sortedKeys.map(key => (
                <div key={key} className="phase-section">
                    <h4
                        className="phase-title clickable"
                        onClick={() => togglePhase(key)}
                        style={{ borderLeftColor: PHASE_STYLE[key]?.color || '#cbd5e1' }}
                    >
                        <i className={`fa fa-chevron-${openPhases[key] ? 'down' : 'right'} phase-chevron`}></i>
                        <i
                            className="fa fa-flag phase-flag"
                            style={{ color: PHASE_STYLE[key]?.color || '#cbd5e1' }}
                        ></i>
                        {phaseLabel(key)}
                        <span className="phase-count">({grouped[key].length})</span>
                        {key !== 'SIN_FASE' && (
                            <span className="phase-pts-badge" style={{ background: PHASE_STYLE[key]?.bg, color: PHASE_STYLE[key]?.color }}>
                                {phasePoints(key)} pts
                            </span>
                        )}
                    </h4>

                    {openPhases[key] && (
                        <>
                            {key !== 'SIN_FASE' && (standings[key] || []).length > 0 && (
                                <div className="standings-block">
                                    <div className="standings-header">
                                        <span className="standings-title">
                                            <i className="fa fa-list-ol"></i> Clasificación
                                        </span>
                                    </div>

                                    <table className="standings-table">
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>Equipo</th>
                                                <th>Pts</th>
                                                {isCaptain && <th></th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(standings[key] || []).map((entry, idx) => (
                                                <tr key={entry.id} className={entry.rivalName === OUR_TEAM_NAME ? 'standing-row--ours' : ''}>
                                                    <td className="standing-pos">{idx + 1}</td>
                                                    <td className="standing-name">
                                                        {entry.rivalName === OUR_TEAM_NAME && (
                                                            <i className="fa fa-shield standing-our-icon"></i>
                                                        )}
                                                        {entry.rivalName}
                                                    </td>
                                                    <td className="standing-pts">
                                                        {editingStandingId === entry.id ? (
                                                            <input
                                                                className="standing-pts-input"
                                                                type="number"
                                                                min="0"
                                                                value={editingPts}
                                                                autoFocus
                                                                onChange={e => setEditingPts(e.target.value)}
                                                                onKeyDown={e => {
                                                                    if (e.key === 'Enter') saveStandingPts(key, entry.id);
                                                                    if (e.key === 'Escape') cancelEditStanding();
                                                                }}
                                                                onClick={e => e.stopPropagation()}
                                                            />
                                                        ) : (
                                                            entry.points
                                                        )}
                                                    </td>
                                                    {isCaptain && (
                                                        <td className="standing-actions">
                                                            {editingStandingId === entry.id ? (
                                                                <>
                                                                    <button
                                                                        className="btn-standing-edit"
                                                                        onClick={() => saveStandingPts(key, entry.id)}
                                                                        title="Guardar"
                                                                    >
                                                                        <i className="fa fa-check"></i>
                                                                    </button>
                                                                    <button
                                                                        className="btn-standing-del"
                                                                        onClick={cancelEditStanding}
                                                                        title="Cancelar"
                                                                    >
                                                                        <i className="fa fa-times"></i>
                                                                    </button>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <button
                                                                        className="btn-standing-edit"
                                                                        onClick={e => startEditStanding(e, entry)}
                                                                        title="Editar puntos"
                                                                    >
                                                                        <i className="fa fa-pencil"></i>
                                                                    </button>
                                                                    <button
                                                                        className="btn-standing-del"
                                                                        onClick={e => handleDeleteStanding(e, key, entry.id)}
                                                                        title="Eliminar"
                                                                    >
                                                                        <i className="fa fa-trash"></i>
                                                                    </button>
                                                                </>
                                                            )}
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            <div className="history-list">
                                {grouped[key].map(match => (
                                    <div key={match.id} className={`history-card-wrapper${isCaptain ? ' captainable' : ''}`}>
                                        <div
                                            className="history-card"
                                            onClick={() => navigate(`/matchdays/${match.id}`)}
                                        >
                                            <div className="match-date">
                                                <span className="day">{formatDate(match.date, { day: 'numeric' })}</span>
                                                <span className="month">{formatDate(match.date, { month: 'short' }).toUpperCase()}</span>
                                            </div>

                                            <div className="match-info">
                                                <span className="opponent">vs {match.opponentName || 'Rival'}</span>
                                                <span className="location">
                                                    <i className="fa fa-map-marker"></i> {match.location}
                                                </span>
                                            </div>

                                            <div className="match-score">
                                                <div className="score-numbers">
                                                    <span className="us">{match.pointsTeam ?? 0}</span>
                                                    <span className="divider">-</span>
                                                    <span className="them">{match.pointsOpponent ?? 0}</span>
                                                </div>
                                                {getResultBadge(match.pointsTeam, match.pointsOpponent)}
                                            </div>

                                            {isCaptain && (
                                                <button
                                                    className="btn-edit-phase"
                                                    onClick={e => startEdit(e, match)}
                                                    title="Cambiar fase"
                                                >
                                                    <i className="fa fa-pencil"></i>
                                                </button>
                                            )}
                                        </div>

                                        {editingId === match.id && (
                                            <div className="assign-panel" onClick={e => e.stopPropagation()}>
                                                <select
                                                    value={editPhase}
                                                    onChange={e => setEditPhase(e.target.value)}
                                                    className="assign-select"
                                                    autoFocus
                                                >
                                                    <option value="">Sin fase</option>
                                                    {phases.map(p => (
                                                        <option key={p.value} value={p.value}>{p.label}</option>
                                                    ))}
                                                </select>
                                                <div className="assign-actions">
                                                    <button className="btn-save-assign" onClick={e => saveEdit(e, match.id)}>
                                                        <i className="fa fa-check"></i> Guardar
                                                    </button>
                                                    <button className="btn-cancel-assign" onClick={cancelEdit}>
                                                        Cancelar
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                        </>
                    )}
                </div>
            ))}
        </div>
    );
}
